import type { AddPreviewCause, AddPreviewSkipReason } from "@nu-assist/contracts";

/**
 * exportConfirmationCopy — every user-facing string the Add panel's
 * confirmation says, in one file.
 *
 * The panel stopped being a roster on Trello #758 and became a confirmation
 * with stats. Its numbers all come from the LAST Compare run, so its wording
 * has to stay answerable to Compare's — collecting the strings here is what
 * makes a drift between the two surfaces a one-file diff instead of a hunt
 * through JSX.
 *
 * English-only (workspace policy).
 */

/** Heading of the blocked state — no usable compare, so no numbers exist. */
export const EXPORT_NOT_COMPARED_TITLE = "Compare to Thrive first";

/** Heading when the fault is OURS, not a missing compare. Separate constant
 *  because "Compare to Thrive first" is an instruction, and instructing someone
 *  to do a thing that will not help is worse than saying nothing. */
export const EXPORT_COMPARE_UNREADABLE_TITLE = "Could not read the last compare";

/**
 * Why the write is held, in the recruiter's terms.
 *
 * It names the CONSEQUENCE ("we cannot tell you what this will do") rather than
 * the mechanism ("no compare run exists"), because the mechanism is ours and
 * the consequence is theirs.
 */
export const EXPORT_NOT_COMPARED_BODY =
  "These contacts have not been checked against Thrive, so there is nothing to show you about what this will do. Run Compare to Thrive first.";

/** The same hold, when the last run FAILED rather than never happening. A
 *  failed run is not a missing one: saying "not compared yet" to someone who
 *  just watched a compare fail reads as the product losing their work. */
export const EXPORT_COMPARE_FAILED_BODY =
  "The last Compare to Thrive did not finish, so there are no results to show. Run it again before adding these contacts.";

/**
 * The read itself failed — a 5xx, a timeout, an edge 403.
 *
 * Deliberately does NOT say "not compared yet". That would blame the recruiter
 * for our outage, and would invite them to run a full dry run to fix something
 * a retry fixes (/pr-review iter 1, Agent A 1).
 */
export const EXPORT_COMPARE_UNREADABLE_BODY =
  "We could not read the last Compare to Thrive, so there is nothing to show you about what this will do.";

/** And when one is in flight — the stored run is blanked while it runs, so
 *  there is genuinely nothing to read yet. */
export const EXPORT_COMPARE_RUNNING_BODY =
  "A Compare to Thrive is running. Wait for it to finish to see what this will do.";

/**
 * Who the run leaves out for want of a recent compare: never compared, or
 * compared longer ago than the window (Trello #823, 24 hours by default, a
 * per-stage setting). The Add is not blocked (sub-task 04): the server sends
 * the rest, so this says before the click who will not go.
 *
 * ONE LINE FOR BOTH CAUSES, counted together (Lucas, 2026-10-29): one
 * `Compare to Thrive` clears both, so the recruiter needs the number, not the
 * split. In the voice of the panel's other notes (`exportNotAddedNote`,
 * `exportTooManySelected`): warning ink, minimum text. A box here read as an
 * alarm (Lucas, 2026-10-28).
 *
 * `total` is the people the recruiter meant to add. "of" when only part of it
 * needs comparing, so the sentence says the others still go.
 */
export function exportNeedsCompareNote(n: number, total: number): string {
  const verb = n === 1 ? "needs" : "need";
  const who = n < total ? `${n} of ${total} contacts` : n === 1 ? "1 contact" : `${n} contacts`;
  return `${who} ${verb} a new comparison with Thrive and will not be added.`;
}

/** The toast for a start the server refused on the same rule, which it does
 *  only when nothing recent was left to send (the preview was read before the
 *  window closed). "Try again" is the wrong advice there: a retry is refused
 *  the same way until a compare runs. */
export const EXPORT_COMPARE_EXPIRED_TOAST =
  "Nothing was added: these contacts were compared too long ago. Run Compare to Thrive again, then add.";

/** The toast for a start the server refused on that rule: only when nothing
 *  correctly compared was left to send. */
export const EXPORT_NOT_COMPARED_TOAST =
  "Nothing was added: some of these contacts were never compared with Thrive. Run Compare to Thrive, then add.";

/** Running a compare — the action for the states where there is nothing to
 *  read yet. NOT every blocked state, which is what this said until iter 5:
 *  `running` withholds it (the way out is to wait, and the server refuses a
 *  reclaim for an hour) and `unreadable` withholds it (the compare probably
 *  exists; we failed to fetch it). */
export const EXPORT_RUN_COMPARE_CTA = "Compare to Thrive";

/** Re-READ, not re-RUN — the action for a read that failed.
 *
 *  Its absence made `unreadable` the dead end `running` had just stopped being:
 *  copy naming a transient fault and telling the recruiter to retry, on a
 *  screen with nothing to retry with (/pr-review iter 2, Agent C N2 / A §3a).
 *
 *  It follows the FAILING READ rather than one state, so a `running` compare
 *  whose polls start failing gets it too (iter 4, Agent A §3a) — with one
 *  exclusion: a 404 (`never`), where re-reading a permanent answer is what
 *  iteration 3 spent a finding learning not to do (iter 5, Agents A and C).
 *
 *  Deliberately not `EXPORT_RUN_COMPARE_CTA`: the compare probably exists and
 *  is fine; we failed to fetch it. */
export const EXPORT_RETRY_READ_CTA = "Try again";

/**
 * The post-run heading.
 *
 * A failed row is named in the HEADING, not in a detail line below it: the
 * heading is the only part of this block a recruiter is guaranteed to read,
 * and a run that lost contacts must not be able to read as a clean success
 * from a glance.
 */
export function exportResultHeading(exported: number, total: number, failed: number): string {
  if (failed > 0) return `Added ${exported} of ${total}. ${failed} failed.`;
  return `Added ${exported} of ${total}`;
}

/** Result line for the rows a run left out because they need a new Compare
 *  (Trello #823). Read from the 202, so it also names rows that aged out
 *  between the panel's preview and the click. */
export function exportResultNeedsCompareLine(n: number): string {
  return `${n} not added: ${n === 1 ? "needs" : "need"} a new comparison with Thrive.`;
}

/** Secondary line for rows that had not settled when the run reached terminal. */
export function exportResultPendingLine(pending: number): string {
  return `${pending} still processing.`;
}

/** Where the per-contact answer lives now that this panel no longer lists it. */
export const EXPORT_RESULT_DETAIL_POINTER =
  "Per-contact results are in the contacts table behind this panel.";

/** The run finished but its rows are not in the payload — outcome unknown. */
export const EXPORT_RESULT_UNKNOWN =
  "The run finished. Open the contacts table to see the results.";

/**
 * The over-cap refusal, on the panel rather than the dialog.
 *
 * A sibling of `preAddTooManySelected`, and it cannot be the same sentence:
 * that one offers "Add all of them instead", which is a radio one click away
 * in the dialog. From here there is no other arm to take — the recruiter has
 * to go back to the table — so the escape has to name the control that is
 * actually reachable, which is the cross-page banner.
 *
 * `selected` is the number of IDS THE REQUEST WOULD CARRY, not the number the
 * sentence above promises: undecided conflicts are subtracted from the promise
 * and still travel, so quoting the promise would name a number that is not the
 * one over the limit.
 */
export function exportTooManySelected(selected: number, max: number): string {
  return `This selection has ${selected} contacts, and a run can carry at most ${max} at a time. Close this, tick the checkbox at the top of the table and choose "Select all" to add the whole list, which has no limit. Or untick some rows and run it in batches.`;
}

/**
 * The trailing clause naming rows a run SENT and did not WRITE.
 *
 * ONE SENTENCE, USED BY EVERY TERMINAL SURFACE, and that is the point of it
 * existing at all. This information was added cell by cell — the page's
 * all-skipped arm, then the hook's all-skipped arm, then the hook's mixed arm —
 * and each time the cells nobody had reached yet kept announcing a run as a
 * clean success while some of its rows came back flagged To review. Two
 * reviewers counted the remaining holes independently (1 of 4, then 1 of 6).
 * A shared clause is the only shape where "fix it everywhere" is one edit
 * rather than a sweep somebody has to remember to finish.
 *
 * Empty when there is nothing to say, so callers can concatenate it blind.
 *
 * NOT the word `skipped`: the table renders these rows To review, and a
 * run-skip displayed as a resolution was the #748 bug.
 *
 * AND NOT "flagged To review" EITHER, SINCE 2026-09-17. That wording named ONE
 * cause, and the never-compared rule gave `skipped` a second, larger one: a row
 * Compare never classified is skipped too, lands in `not_added` rather than
 * `needs_review` (`_add_to_search_state_case`), and sending its recruiter to
 * Compare "to decide it" sends them to decide nothing. This PR fixed exactly
 * that reading on the PRE-run note and left it here on the POST-run toast
 * (/pr-review iter 5, Agent C).
 *
 * The honest fix is not a better guess. `ContactExportResult` carries no skip
 * reason and the reason is not persisted per contact — only `export_error` is —
 * so the toast CANNOT know which cause applies without a migration this card is
 * not taking. The list can: its status cell reads the server's `review_status`,
 * which says "Not compared yet" for one and "identity mismatch" for the other.
 * So the toast states the count and points there.
 */
export function exportNotAddedClause(skipped: number): string {
  if (skipped <= 0) return "";
  return ` ${skipped} other${skipped === 1 ? " was" : "s were"} not added. Open the contacts table to see why.`;
}

/**
 * The whole sentence for a run that wrote NOTHING and sent something.
 *
 * Its own function rather than the clause above with a prefix: "Added 0
 * contacts" is the thing being replaced, so there is no first half to append to.
 *
 * ALSO THE PANEL'S RESULT-CARD LINE. That card used to carry a third wording of
 * this same fact — `exportResultSkippedLine`, a standalone `<li>` saying the
 * same thing in different words — which is precisely what the clause above was
 * introduced to stop, on the surface directly beside it. Two sentences for one
 * outcome is how a recruiter ends up unsure whether they are two outcomes
 * (/pr-review iter 4, Agent B). One appendable clause, one standalone sentence,
 * and nothing else.
 */
export function exportNothingWrittenLine(skipped: number): string {
  // SAME CORRECTION AS THE CLAUSE ABOVE, for the same reason: this asserted
  // "an unresolved conflict" for every skipped row, and since 2026-09-17 a row
  // nobody compared is skipped too. Neither this line nor the poll response can
  // tell the two apart, so it stops claiming to.
  return `${skipped} contact${skipped === 1 ? " was" : "s were"} not added. Open the contacts table to see why.`;
}

/**
 * Why some of the selection will not be added — one clause per cause.
 *
 * SIX REASONS, FOUR REMEDIES, and the remedies are why the server publishes the
 * causes separately instead of handing over one subtraction. "Nobody ever
 * compared this row" is fixed by running Compare; "somebody must decide a
 * conflict" is fixed by opening Compare and choosing; "it is already in Thrive"
 * needs nothing at all. Collapsing them into a single number is the documented
 * defect on this card, and the sentence this replaces did worse than collapse
 * them: it asserted the conflict reason for every case, so a selection of
 * never-compared contacts was told to go and decide conflicts that do not
 * exist.
 *
 * W2 and W3 ARE grouped, and only they — they share a remedy exactly, and the
 * distinction between an identity mismatch and an unverified name match is
 * Compare's to explain, on the screen where the recruiter decides it.
 *
 * The map is keyed by the generated `AddPreviewSkipReason` union, so a seventh
 * reason added server-side fails the BUILD here. At RUNTIME a newer api can
 * still send one to an older bundle, and that case degrades to a bare count
 * rather than throwing — see the lookup below.
 */
const NOT_ADDED_CLAUSES: Record<AddPreviewSkipReason, (n: number) => string> = {
  not_compared_no_thrive_check: (n) => `${n} never compared`,
  compare_too_old: (n) => `${n} compared too long ago`,
  w2_identity_mismatch_skipped: (n) => `${n} unresolved conflict${n === 1 ? "" : "s"}`,
  w3_unverified_name_match_skipped: (n) => `${n} unresolved conflict${n === 1 ? "" : "s"}`,
  duplicate_linkedin_fix_in_thrive_first: (n) => `${n} duplicate${n === 1 ? "" : "s"} in Thrive`,
  left_out_dismissed: (n) => `${n} left out`,
  already_exported_idempotent: (n) => `${n} already in Thrive`,
};

/**
 * `null` when everything in scope will be added, which is the common path and
 * where a permanent "0 will not be added" row would be noise.
 *
 * AS SHORT AS THE FACTS ALLOW (operator, 2026-09-17: warning styling, minimum
 * text). Each cause is a count and a noun. The remedies used to ride along in
 * the same sentence and made it four lines of prose; the recruiter already
 * knows what Compare is, and the button beside this is disabled when none of
 * the selection can be added, which says the important half without words.
 */
export function exportNotAddedNote(
  causes: readonly AddPreviewCause[],
  { omit = [] }: { omit?: readonly AddPreviewSkipReason[] } = {}
): string | null {
  // `omit`: causes another line already states. The panel names the two
  // Compare causes in one line of their own, with a Compare to Thrive button
  // under it (sub-task 04), so counting them here too would say the same rows
  // twice.
  const shown = causes.filter((c) => !omit.includes(c.reason));
  if (shown.length === 0) return null;
  // W2 and W3 render as ONE clause: they share a remedy exactly, and the
  // difference between an identity mismatch and an unverified name match is
  // Compare's to explain, on the screen where the recruiter decides it.
  const merged = new Map<string, number>();
  for (const c of shown) {
    const key =
      c.reason === "w3_unverified_name_match_skipped" ? "w2_identity_mismatch_skipped" : c.reason;
    merged.set(key, (merged.get(key) ?? 0) + c.count);
  }
  const clauses = [...merged].map(([reason, n]) => {
    // A LOOKUP, THEN A FALLBACK — not a cast into a call.
    //
    // The Record's key type gives COMPILE-TIME exhaustiveness: a seventh reason
    // added to the generated union fails the build here, which is why the
    // server publishes causes as a `Literal`. It gives nothing at RUNTIME. The
    // api and the web bundle ship separately, so a newer server can send a
    // reason this bundle has never heard of, and `NOT_ADDED_CLAUSES[x]` is then
    // `undefined` — calling it throws and takes the panel down.
    //
    // The docstring above used to say such a reason would "render blank". It
    // would have crashed (/pr-review iter 5, Agent A). It degrades to a count
    // instead: the recruiter loses the WHY for that row and keeps the number,
    // and the total still reconciles against `will_not_add`.
    const clause = NOT_ADDED_CLAUSES[reason as AddPreviewSkipReason];
    return clause ? clause(n) : `${n} not added`;
  });
  return `Not added: ${clauses.join(", ")}.`;
}

export const EXPORT_PREVIEW_UNAVAILABLE =
  "We could not check what this run would do. Try again in a moment.";
